import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFlowObserverPanelStore, adaptHistoricalExecutionToLogEntries, deriveHistoricalStatusLabel } from '@flow-studio/flow-observer-core';
import type { FlowObserverTabContentProps, ExecutionLogEntry } from '@flow-studio/flow-observer-core';
import { useWorkflowStore, useExecutionHistoryStore, useDesignerModeStore } from '@flow-studio/store';
import { DataItemsView } from '../common/DataItemsView.js';
import { useLocalStorage } from '../../hooks/useLocalStorage.js';
import './NodesHierarchyTabContent.css';

interface EntityNode {
  id: number;
  type: 'Process' | 'ProcessThread' | 'ProcessElement';
  name: string;
  key: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  elapsedMs?: number;
  errorMessage?: string;
  children: EntityNode[];
}

function buildEntityHierarchy(logs: ExecutionLogEntry[], fallbackWorkflowName?: string): EntityNode[] {
  const entities = new Map<string, EntityNode>();
  const processes = new Map<number, EntityNode>();
  const threads = new Map<number, EntityNode>();

  logs.forEach((log) => {
    const item = (log.rawDetail as any)?.item;
    const status = (log.rawDetail as any)?.status;

    if (!item) return;

    const entityKey = `${item.entityType}-${item.id}`;

    // The structured eventInfo carries the real process/thread name reliably — the
    // WorkflowStarted event's item.name is often just a placeholder ("Unknown") before
    // the backend resolves it, but currentContext already has the definitive name.
    const currentContext = (log.eventInfo as any)?.currentContext;
    const resolvedName: string | undefined =
      item.entityType === 'Process'
        ? currentContext?.process?.operatingContext?.myProcessDefinition?.process?.name
        : item.entityType === 'ProcessThread'
        ? currentContext?.processThread?.operatingContext?.myProcessThreadDefinition?.thread?.name
        : undefined;
    // A ProcessThread IS the workflow being executed — if the backend never resolves a
    // name for it in any event, fall back to the workflow name already loaded for the
    // open Designer canvas rather than showing the raw placeholder.
    const threadFallback = item.entityType === 'ProcessThread' ? fallbackWorkflowName : undefined;
    const bestName = (resolvedName && resolvedName !== 'Unknown')
      ? resolvedName
      : (item.name && item.name !== 'Unknown')
      ? item.name
      : threadFallback || item.name;

    if (!entities.has(entityKey)) {
      entities.set(entityKey, {
        id: item.id,
        type: item.entityType,
        name: bestName,
        key: item.key,
        status: status?.runState || 'unknown',
        elapsedMs: status?.elapsedMs,
        children: [],
      });
    }

    const entity = entities.get(entityKey)!;
    entity.status = status?.runState || entity.status;
    entity.elapsedMs = status?.elapsedMs ?? entity.elapsedMs;
    // Adopt a later, more specific name once one shows up, instead of locking in
    // whatever placeholder the entity's first-seen event happened to carry.
    if (bestName && bestName !== 'Unknown' && bestName !== entity.name) {
      entity.name = bestName;
    }

    if (item.entityType === 'Process') {
      processes.set(item.id, entity);
    } else if (item.entityType === 'ProcessThread') {
      threads.set(item.id, entity);
    }
  });

  // Build hierarchy
  const root: EntityNode[] = [];
  processes.forEach((process) => {
    threads.forEach((thread) => {
      const isChildOfProcess = logs.some(l => {
        const item = (l.rawDetail as any)?.item;
        return item?.entityType === 'ProcessThread' && item?.id === thread.id;
      });

      if (isChildOfProcess) {
        process.children.push(thread);

        // Add ProcessElements as children of threads
        logs.forEach((log) => {
          const item = (log.rawDetail as any)?.item;
          if (item?.entityType === 'ProcessElement') {
            const nodeEntity = entities.get(`ProcessElement-${item.id}`);
            if (nodeEntity && !thread.children.includes(nodeEntity)) {
              thread.children.push(nodeEntity);
            }
          }
        });
      }
    });

    root.push(process);
  });

  // Some executions never emit a top-level 'Process' entity event — only ProcessThread/
  // ProcessElement events arrive. Without this fallback, `root` stays empty forever
  // (it's only ever populated by iterating `processes` above) regardless of how much real
  // thread/node activity is sitting in `entities`/`threads`. Treat un-parented threads as
  // top-level roots instead of silently dropping everything.
  if (root.length === 0 && threads.size > 0) {
    threads.forEach((thread) => {
      logs.forEach((log) => {
        const item = (log.rawDetail as any)?.item;
        if (item?.entityType === 'ProcessElement') {
          const nodeEntity = entities.get(`ProcessElement-${item.id}`);
          if (nodeEntity && !thread.children.includes(nodeEntity)) {
            thread.children.push(nodeEntity);
          }
        }
      });
      root.push(thread);
    });
  }

  return root;
}

function formatMs(ms?: number): string {
  if (!ms) return '—';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

const statusColorMap: Record<string, string> = {
  'completed': '#10b981',
  'running': '#3b82f6',
  'failed': '#ef4444',
  'skipped': '#9ca3af',
  'idle': '#d1d5db',
};

interface TreeNodeProps {
  entity: EntityNode;
  level: number;
}

const TreeNode: React.FC<TreeNodeProps> = ({ entity, level }) => {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = entity.children.length > 0;
  const statusColor = statusColorMap[entity.status] || '#d1d5db';

  return (
    <div className="tree-node">
      <div
        className="tree-node__header"
        style={{ paddingLeft: `${level * 16}px` }}
        onClick={() => hasChildren && setExpanded(!expanded)}
      >
        {hasChildren && (
          <span className={`tree-node__toggle ${expanded ? 'open' : ''}`}>
            ▶
          </span>
        )}
        {!hasChildren && <span className="tree-node__toggle placeholder" />}

        <span
          className="tree-node__status-dot"
          style={{ backgroundColor: statusColor }}
        />

        <span className="tree-node__name">
          {entity.name}
        </span>

        <span className="tree-node__type">
          {entity.type}
        </span>

        {entity.elapsedMs !== undefined && (
          <span className="tree-node__timing">
            {formatMs(entity.elapsedMs)}
          </span>
        )}
      </div>

      {expanded && hasChildren && (
        <div className="tree-node__children">
          {entity.children.map((child) => (
            <TreeNode key={`${child.type}-${child.id}`} entity={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * "Executions" folder — a sibling to the live Process tree, browsing past runs via the
 * already-built (previously zero-consumer) useExecutionHistoryStore/processThreadExecutionApiClient
 * rather than a new implementation. Only ExecutionHistoryItem's aggregate fields are shown —
 * getById doesn't return per-node event data, so this is not a reconstruction of the live grid.
 */
const ExecutionsFolder: React.FC<{ processThreadId: number | null }> = ({ processThreadId }) => {
  const [expanded, setExpanded] = useState(false);
  const history = useExecutionHistoryStore((s) => s.history);
  const total = useExecutionHistoryStore((s) => s.total);
  const isLoading = useExecutionHistoryStore((s) => s.isLoading);
  const loadHistory = useExecutionHistoryStore((s) => s.loadHistory);
  const loadMore = useExecutionHistoryStore((s) => s.loadMore);
  const selectedHistoryExecutionId = useDesignerModeStore((s) => s.selectedHistoryExecutionId);
  const selectHistoryExecution = useDesignerModeStore((s) => s.selectHistoryExecution);
  const clearSelectedHistoryExecution = useDesignerModeStore((s) => s.clearSelectedHistoryExecution);

  const handleToggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && processThreadId != null && history.length === 0) {
      loadHistory(processThreadId);
    }
    if (!next) {
      // The detail pane belongs to a row inside this folder, but it lives as a sibling
      // driven by designerModeStore — collapsing the folder left it docked at the bottom
      // with no visible row to explain it. Worse, the selection is store-level, so a
      // stale one kept stealing the pane's height share across remounts. Dismiss it
      // with the rows that own it.
      clearSelectedHistoryExecution();
    }
  };

  return (
    <div className="tree-node">
      <div
        className="tree-node__header"
        style={{ paddingLeft: 0 }}
        onClick={handleToggle}
      >
        <span className={`tree-node__toggle ${expanded ? 'open' : ''}`}>▶</span>
        <span className="tree-node__status-dot" style={{ backgroundColor: '#9ca3af' }} />
        <span className="tree-node__name">Executions</span>
        {total > 0 && <span className="tree-node__type">{total}</span>}
      </div>

      {expanded && (
        <div className="tree-node__children">
          {processThreadId == null ? (
            <div className="fop-empty"><span>No workflow context to browse executions for</span></div>
          ) : isLoading && history.length === 0 ? (
            <div className="fop-empty"><span>Loading executions…</span></div>
          ) : history.length === 0 ? (
            <div className="fop-empty"><span>No past executions found</span></div>
          ) : (
            <>
              {history.map((item) => {
                const statusLabel = deriveHistoricalStatusLabel(item);
                return (
                  <div
                    key={item.processThreadExecutionId}
                    className="tree-node__header"
                    style={{ paddingLeft: '16px' }}
                    onClick={() => selectHistoryExecution(item.processThreadExecutionId)}
                  >
                    <span className="tree-node__toggle placeholder" />
                    <span
                      className="tree-node__status-dot"
                      style={{ backgroundColor: statusColorMap[statusLabel.toLowerCase()] || '#d1d5db' }}
                    />
                    <span
                      className="tree-node__name"
                      style={{ fontWeight: selectedHistoryExecutionId === item.processThreadExecutionId ? 700 : 400 }}
                    >
                      {statusLabel} — {item.startedAt ? new Date(item.startedAt).toLocaleString() : 'Unknown time'}
                    </span>
                    <span className="tree-node__type">{item.completedNodes ?? 0}/{item.totalNodes ?? 0} nodes</span>
                    <span className="tree-node__timing">{formatMs(item.duration)}</span>
                  </div>
                );
              })}
              {history.length < total && (
                <button
                  type="button"
                  className="tree-node__header nodes-hierarchy__load-more"
                  style={{ paddingLeft: '16px', width: '100%', border: 'none', background: 'none', textAlign: 'left' }}
                  onClick={() => loadMore()}
                  disabled={isLoading}
                >
                  <span className="tree-node__toggle placeholder" />
                  <span className="tree-node__name">{isLoading ? 'Loading…' : 'Load more'}</span>
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

/** Aggregate-summary detail view for the selected historical execution — see the module doc comment on ExecutionsFolder. */
const HistoricalExecutionDetail: React.FC<{ heightPct: number }> = ({ heightPct }) => {
  const summary = useDesignerModeStore((s) => s.selectedHistoricalExecutionSummary);
  const isLoading = useDesignerModeStore((s) => s.isLoadingHistoricalExecutionSummary);
  const loadError = useDesignerModeStore((s) => s.selectedHistoricalExecutionError);
  const selectedHistoryExecutionId = useDesignerModeStore((s) => s.selectedHistoryExecutionId);
  const clearSelectedHistoryExecution = useDesignerModeStore((s) => s.clearSelectedHistoryExecution);

  if (!selectedHistoryExecutionId) return null;

  const adapted = summary ? adaptHistoricalExecutionToLogEntries(summary) : [];
  const summaryForDisplay = adapted[0]?.rawDetail ?? {};

  return (
    <div className="nodes-hierarchy__historical-detail" style={{ height: `${heightPct}%`, maxHeight: 'none' }}>
      <div className="nodes-hierarchy__historical-detail__header">
        <span>Execution {selectedHistoryExecutionId}</span>
        <button type="button" onClick={clearSelectedHistoryExecution}>×</button>
      </div>
      {isLoading && <div className="fop-empty"><span>Loading execution summary…</span></div>}
      {!isLoading && loadError && (
        <div className="fop-empty" style={{ color: '#ef4444' }}>
          <span>Failed to load execution summary: {loadError}</span>
        </div>
      )}
      {!isLoading && !loadError && summary && <DataItemsView items={[summaryForDisplay]} />}
    </div>
  );
};

export const NodesHierarchyTabContent: React.FC<FlowObserverTabContentProps> = ({
  tabId,
  loadState,
  error,
  onRetry,
}) => {
  const logBuffer = useFlowObserverPanelStore(
    (s) => s.tabRuntimeStates['execution-logs']?.dataBuffer ?? []
  );
  const workflowName = useWorkflowStore((s) => s.workflow?.name);
  const processThreadId = useWorkflowStore((s) => s.currentProcessThreadId);
  const selectedHistoryExecutionId = useDesignerModeStore((s) => s.selectedHistoryExecutionId);

  const entityTree = useMemo(() => {
    return buildEntityHierarchy(logBuffer as ExecutionLogEntry[], workflowName);
  }, [logBuffer, workflowName]);

  // Drag-resize for the tree/historical-detail split — same mousedown->window-listener->clamped-percentage
  // pattern as ExecutionLogsTabContent's list/detail split, just resizing height instead of width.
  const [detailHeightPct, setDetailHeightPct] = useLocalStorage<number>('nodes-hierarchy-detail-height', 35);
  const [isResizingDetail, setIsResizingDetail] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleDetailResizeStart = useCallback(() => {
    setIsResizingDetail(true);
  }, []);

  useEffect(() => {
    if (!isResizingDetail) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const fromBottom = rect.bottom - e.clientY;
      const pct = (fromBottom / rect.height) * 100;
      setDetailHeightPct(Math.max(15, Math.min(80, pct)));
    };
    const handleMouseUp = () => setIsResizingDetail(false);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizingDetail, setDetailHeightPct]);

  if (loadState === 'loading') {
    return <div className="fop-loading">Loading…</div>;
  }

  if (loadState === 'error' && error) {
    return (
      <div className="fop-error" role="alert">
        <span>{error.message}</span>
        {error.retryable && (
          <button className="fop-error__retry" onClick={onRetry}>Retry</button>
        )}
      </div>
    );
  }

  return (
    <div className="nodes-hierarchy" ref={containerRef}>
      <div className="nodes-hierarchy__container">
        {entityTree.length === 0 ? (
          <div className="fop-empty">
            <span>No entities executed yet</span>
          </div>
        ) : (
          entityTree.map((process) => (
            <TreeNode key={`Process-${process.id}`} entity={process} level={0} />
          ))
        )}
        <ExecutionsFolder processThreadId={processThreadId} />
      </div>
      {selectedHistoryExecutionId && (
        <div
          className="nodes-hierarchy__detail-divider"
          onMouseDown={handleDetailResizeStart}
          title="Drag to resize"
          role="separator"
          aria-orientation="horizontal"
          style={{ cursor: isResizingDetail ? 'row-resize' : undefined }}
        />
      )}
      <HistoricalExecutionDetail heightPct={detailHeightPct} />
    </div>
  );
};
